import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { currentLanguage, LANGUAGE_CODES, LANGUAGE_NAMES, setLanguage, SUPPORTED_LANGUAGES } from 'src/i18n';
import type { SupportedLanguage } from 'src/i18n/types';

/**
 * Kompakt språkvelger (issue #13280).
 *
 * Viser språkene som korte koder (NO, EN, SV, DA, PL). Kodene flyter som
 * inline-tekst og wrapper til flere linjer ved behov (i motsetning til en
 * flex-rad, som ville prøvd å holde alt på én linje og blitt klippet i en smal
 * meny). Aktivt språk er uthevet (bold). Klikk bytter språk og laster siden på
 * nytt (se `setLanguage`); klikket stopper propagering slik at en omsluttende
 * meny (brukermenyens backdrop) ikke svelger byttet.
 *
 * Med `label` (f.eks. "Språk") rendres kodene i forlengelsen av ledeteksten
 * ("Språk: NO EN …") med et hengende innrykk på linjer som wrapper. Brukes både
 * i brukermenyen (med label) og før innlogging (d-login, `outskirts`-variant).
 */
@customElement('d-language-selector')
export class DLanguageSelector extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      font-size: 14px;
      line-height: 1.7;
    }
    /* Hengende innrykk: «Språk:» + kodene på første linje, wrappede linjer
       rykkes litt inn. */
    .line {
      padding-left: 1.6em;
      text-indent: -1.6em;
    }
    .label {
      color: var(--text-color, #222);
    }
    button {
      display: inline-block;
      background: none;
      border: none;
      padding: 0;
      margin: 0 0.9em 0 0;
      font: inherit;
      font-size: 14px;
      letter-spacing: 0.04em;
      cursor: pointer;
      color: var(--linkColorGray, #555);
      opacity: 0.85;
    }
    .label + button {
      margin-left: 0.4em;
    }
    button:hover {
      opacity: 1;
      color: var(--themeColorDarkerOne, #097);
    }
    button[aria-current='true'] {
      font-weight: 700;
      opacity: 1;
      cursor: default;
      color: var(--themeColorDarkerOne, #097);
    }
    /* Lys variant for den mørke innloggingssiden. */
    :host([outskirts]) button {
      color: white;
      opacity: 0.7;
    }
    :host([outskirts]) button:hover,
    :host([outskirts]) button[aria-current='true'] {
      color: white;
      opacity: 1;
    }
  `;

  /** Lys variant for bruk på den mørke innloggingssiden. */
  @property({ type: Boolean, reflect: true })
  outskirts = false;

  /** Valgfri ledetekst (f.eks. «Språk») som kodene flyter videre fra. */
  @property({ type: String })
  label = '';

  private select(event: Event, language: SupportedLanguage) {
    event.stopPropagation();
    if (language !== currentLanguage()) {
      setLanguage(language);
    }
  }

  private renderButtons() {
    const active = currentLanguage();
    return SUPPORTED_LANGUAGES.map(
      (language) =>
        html`<button
          type="button"
          aria-current=${language === active ? 'true' : 'false'}
          title=${LANGUAGE_NAMES[language]}
          @click=${(event: Event) => this.select(event, language)}
        >
          ${LANGUAGE_CODES[language]}
        </button>`,
    );
  }

  render() {
    if (this.label) {
      return html`<div class="line"><span class="label">${this.label}:</span> ${this.renderButtons()}</div>`;
    }
    return html`${this.renderButtons()}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-language-selector': DLanguageSelector;
  }
}
